import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine } from '@remixicon/react';

import styles from './ModalComponent.module.css';
import AnimatedWrapper from '../../layouts/AnimatedWrapper';

export enum ModalVariant {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  className?: string;
  headerIcon?: JSX.Element;
  leftOffset?: number;
  variant?: ModalVariant;
  headerStyle?: string;
}

const variantStyleConfig = {
  [ModalVariant.Small]: {
    class: styles['modal-small'],
  },
  [ModalVariant.Medium]: {
    class: styles['modal-medium'],
  },
  [ModalVariant.Large]: {
    class: styles['modal-large'],
  },
};
const ModalComponent: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  headerIcon,
  leftOffset,
  variant = ModalVariant.Large,
  headerStyle = '',
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalStyle = variantStyleConfig[variant];

  return createPortal(
    <div
      className={styles.overlay}
      style={{ marginLeft: leftOffset }}
      onClick={handleBackdropClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClose();
        }
      }}
      role='button'
      tabIndex={0}
    >
      <AnimatedWrapper isVisible={isOpen} animation='slideUp' duration='0.3s'>
        <div className={`${styles.modal} ${modalStyle.class} ${className}`}>
          {title && (
            <header
              className={
                headerStyle
                  ? `${styles.header} ${headerStyle}`
                  : `${styles.header} ${styles.headerDefault}`
              }
            >
              <h2 className={styles.title}>{title}</h2>
              {headerIcon && headerIcon}
            </header>
          )}

          <button className={styles.closeButton} onClick={onClose}>
            <RiCloseLine
              color='#18181B'
              className={styles.closeIcon}
              size={24}
            />
          </button>

          <div className={styles.content}>{children}</div>
        </div>
      </AnimatedWrapper>
    </div>,
    document.body
  );
};

export default ModalComponent;
